import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "./DayPerformance.css";

const DayPerformance = ({ trend, onBack, marketId }) => {
  const effectiveMarketId = marketId;
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState({});
const isBankNifty = trend.toUpperCase().includes("BANK");
  // 🗓️ Today's date in DD-MM-YYYY
  const getTodayDate = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // 🧠 Fetch companies
  const fetchStocks = async () => {
    setLoading(true);
    try {
      const isBankNifty = trend.toUpperCase().includes("BANK");
      let response;

      if (isBankNifty) {
        // 🏦 Bank Nifty companies from local backend
        response = await fetch("https://prod-erp.nifty10.in/api/banknifty/company/list");
      } else {
        // 📈 Other markets
        response = await fetch("https://prod-erp.nifty10.in/get/company");
      }

      const data = await response.json();

      if (data?.data && Array.isArray(data.data)) {
        if (isBankNifty) {
          // 🔍 Only Big Bull & Big Bear
          const allCompanies = data.data;

          const bigBull = allCompanies.find(
            (c) => c.liveBB === true && c.companyStatus?.toUpperCase() === "BULLISH"
          );
          const bigBear = allCompanies.find(
            (c) => c.liveBB === true && c.companyStatus?.toUpperCase() === "BEARISH"
          );

          const filtered = [];
          if (bigBull) filtered.push({ ...bigBull, label: "Big Bull" });
          if (bigBear) filtered.push({ ...bigBear, label: "Big Bear" });

          setStocks(filtered);
        } else {
          // 📊 Normal flow for Bullish / Bearish / Nifty
          const filteredStocks = data.data.filter(
            (stock) =>
              stock?.liveBB === true &&
              stock?.companyStatus?.toUpperCase() === trend
          );
          setStocks(filteredStocks.slice(0, 5));
        }
      } else {
        console.error("Unexpected API response format", data);
        setStocks([]);
      }
    } catch (error) {
      console.error(`Error fetching ${trend} stocks:`, error);
      toast.error("Failed to fetch company list.");
      setStocks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
  }, [trend, effectiveMarketId]);

  // 🎯 Input handler
  const handleInputChange = (companyId, value) => {
    setPerformanceData((prev) => ({
      ...prev,
      [companyId]: value,
    }));
  };

  // 🚀 Submit results
const handleSubmit = async () => {
  if (!effectiveMarketId) {
    toast.error("Market ID missing.");
    return;
  }

  const payload = stocks.map((stock) => ({
    companyId: stock.companyId,
    companyName: stock.companyName,
    dayPerformance: parseFloat(performanceData[stock.companyId]) || 0,
  }));

  try {
    if (isBankNifty) {
      // 🔹 Call your backend route instead of dev API directly
      const response = await fetch("https://prod-erp.nifty10.in/api/banknifty/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payload,
          userId: "556c3d52-e18d-11ef-9b7f-02fd6cfaf985"
        }),
      });

      const data = await response.json();
      toast.success("Bank Nifty result submitted!");
      setPerformanceData({});
    } else {
      // Normal markets
      const response = await fetch(
        "https://prod-erp.nifty10.in/bids/submit-market-result",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            stocks,
            trend,
            effectiveMarketId,
            performanceData,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to submit data");
      toast.success("Result submitted successfully!");
      setPerformanceData({});
    }
  } catch (err) {
    console.error(err);
    toast.error("Submission failed.");
  }
};


  return (
    <div className="day-performance-container">
      <div className="performance-header">
        <button className="performance-back-btn" onClick={onBack}>
          &larr;
        </button>
        <h1 className="results-title">{trend}</h1>
      </div>

      <h2 className="performance-subtitle">
        Add Day Performance For Final Result
      </h2>

      {loading ? (
        <div className="performance-loading-container">
          <div className="performance-loader"></div>
        </div>
      ) : stocks.length > 0 ? (
        <table className="performance-table">
          <thead>
            <tr>
              <th>{isBankNifty ? "Company" : "Company"}</th>
              <th>Day Performance (%)</th>
            </tr>
          </thead>
          <tbody>
            {stocks.map((stock) => (
              <tr key={stock.companyId} className={stock.label === "Big Bull" ? "bull-row" : stock.label === "Big Bear" ? "bear-row" : ""}>
                <td>
                  {isBankNifty
                    ? `${stock.label} - ${stock.companyName}`
                    : stock.companyName}
                </td>
                <td>
                  <input
                    type="number"
                    className="performance-input"
                    min="0"
                    step="0.01"
                    value={performanceData[stock.companyId] ?? ""}
                    required
                    onChange={(e) =>
                      handleInputChange(stock.companyId, e.target.value)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="no-data-message">No {trend} stocks available</p>
      )}

      <button
        className="performance-submit-btn"
        onClick={handleSubmit}
        disabled={
          loading ||
          stocks.length === 0 ||
          !stocks.every(
            (stock) =>
              performanceData[stock.companyId] !== undefined &&
              performanceData[stock.companyId] !== ""
          )
        }
      >
        Submit
      </button>
    </div>
  );
};

export default DayPerformance;
